// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { isMultilineAuditValue } from '@/audit/utils/is-multiline-audit-value';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography, TypographyProps } from '@mui/material';
import { DateTime } from 'luxon';

const DEFAULT_VARIANT = 'caption';

interface Props<Value> extends Pick<TypographyProps, 'variant'> {
  /**
   * The name of the property that was updated.
   */
  name: string;

  /**
   * The old value of the property.
   */
  oldValue: Value | null;

  /**
   * The new value of the property.
   */
  newValue: Value | null;

  /**
   * If provided, a function that returns a label for the given value.
   */
  getLabelForValue?: (value: Value) => string;
}

/**
 * A component that renders a label for an updated property with an arrow
 * indicating the change.
 */
export function AuditPropertyUpdateLabel<
  Value extends string | number | boolean | DateTime,
>(props: Props<Value>) {
  const variant = props.variant ?? DEFAULT_VARIANT;
  const oldValue =
    props.oldValue === null
      ? null
      : (props.getLabelForValue?.(props.oldValue) ?? props.oldValue);

  const newValue =
    props.newValue === null
      ? null
      : (props.getLabelForValue?.(props.newValue) ?? props.newValue);

  if (isMultilineAuditValue(oldValue) || isMultilineAuditValue(newValue)) {
    return (
      <Stack spacing={0.5}>
        <Typography variant={variant} fontWeight="bold">
          {props.name}:
        </Typography>
        <Stack spacing={0.25} paddingLeft={1}>
          <Typography variant={variant} color="text.secondary">
            Previous
          </Typography>
          <AuditPropertyValue value={oldValue} variant={variant} />
        </Stack>
        <Stack spacing={0.25} paddingLeft={1}>
          <Typography variant={variant} color="text.secondary">
            Current
          </Typography>
          <AuditPropertyValue value={newValue} variant={variant} />
        </Stack>
      </Stack>
    );
  }

  return (
    <Typography display="flex" alignItems="center" variant={variant}>
      <Typography component="span" variant="inherit" fontWeight="bold">
        {props.name}:&nbsp;
      </Typography>
      <AuditPropertyValue value={oldValue} />
      &nbsp;
      <ArrowForwardRounded fontSize="inherit" />
      &nbsp;
      <AuditPropertyValue value={newValue} />
    </Typography>
  );
}
